// One condition of a point based rule, on one line: field, verb, values.
//
// The unflagged page gives each condition a full-width form row built from store
// handlers keyed by three indexes. This takes the condition and hands back a
// changed copy, so the page owns one document and every edit is a replacement.
// The field and verb lists are the same ones that form offers, from the same
// settings, so a rule written here is one that page can open.
import React from 'react';
import { Button, Form } from 'react-bootstrap';

import PointBasedConditionDataModel from '../../../models/pointBasedForm/PointBasedConditionDataModel';
import { ModelType } from '../../../models/ModelItem';
import {
  getVerbSettings,
  getVerbsSettings,
  Subject,
  Verb,
} from '../../../models/sqlform';
import { computationDisplayName } from '../../../utils';

const verbsSettings = getVerbsSettings();

function verbsFor(type: string, modelType: ModelType) {
  return verbsSettings.filter(
    ({ modelTypes, characteristics: { types } }) =>
      modelTypes.includes(modelType) && types.includes(type)
  );
}

export default function PointRuleCondition({
  condition,
  formFields,
  modelType,
  disabled,
  first,
  onChange,
  onRemove,
}: {
  condition: PointBasedConditionDataModel;
  formFields: Subject[];
  modelType: ModelType;
  disabled?: boolean;
  /** The first condition reads "if", later ones "and". */
  first: boolean;
  onChange: (condition: PointBasedConditionDataModel) => void;
  /** Absent on a rule's only condition: a rule needs one. */
  onRemove?: () => void;
}) {
  const { trait, verb, values = [], isLower } = condition;
  const settings = getVerbSettings(verb);
  const max = settings?.characteristics.max ?? 0;
  const min = settings?.characteristics.min ?? 0;

  const changeField = (subject: string) => {
    const field = formFields.find((each) => each.subject === subject);
    if (!field) return;
    // A different type can make the verb and the values meaningless: "greater
    // than" on a text field, "Retailing" in a number. Reset them only then, so
    // swapping one text field for another keeps what was typed.
    if (field.type === trait?.type) {
      onChange({ ...condition, trait: field });
      return;
    }
    const nextVerb = verbsFor(field.type, modelType)[0]?.value ?? 'is';
    onChange({
      ...condition,
      trait: field,
      verb: nextVerb,
      values: Array(getVerbSettings(nextVerb)?.characteristics.min ?? 1).fill(
        ''
      ),
    });
  };

  const changeVerb = (next: Verb) => {
    const nextSettings = getVerbSettings(next);
    const keep = values.slice(0, nextSettings?.characteristics.max ?? 0);
    const floor = nextSettings?.characteristics.min ?? 0;
    onChange({
      ...condition,
      verb: next,
      values:
        keep.length >= floor
          ? keep
          : [...keep, ...Array(floor - keep.length).fill('')],
    });
  };

  const changeValue = (index: number, value: string) =>
    onChange({
      ...condition,
      values: values.map((each, at) => (at === index ? value : each)),
    });

  return (
    <div className="prc">
      <span className="prc-join">{first ? 'if' : 'and'}</span>
      <Form.Select
        size="sm"
        className="prc-field"
        aria-label="Field"
        data-testid="rules-condition-field"
        disabled={disabled}
        value={trait?.subject ?? ''}
        onChange={(event) => changeField(event.target.value)}
      >
        {formFields.map((field) => (
          <option key={field.subject} value={field.subject}>
            {computationDisplayName(field.subject)}
          </option>
        ))}
      </Form.Select>
      <Form.Select
        size="sm"
        className="prc-verb"
        aria-label="Condition"
        data-testid="rules-condition-verb"
        disabled={disabled}
        value={verb}
        onChange={(event) => changeVerb(event.target.value as Verb)}
      >
        {verbsFor(trait?.type, modelType).map(({ value }) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </Form.Select>
      {values.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span className="prc-value" key={index}>
          <Form.Control
            size="sm"
            // As the unflagged form does: a date field gets a date picker,
            // so a rule cannot carry a date the SQL reads some other way.
            type={
              trait?.type === 'string' || !trait?.type ? 'text' : trait.type
            }
            aria-label="Value"
            data-testid="rules-condition-value"
            disabled={disabled}
            value={value ?? ''}
            isInvalid={!String(value ?? '').trim()}
            onChange={(event) => changeValue(index, event.target.value)}
          />
          {values.length > min && (
            <button
              type="button"
              className="prc-x"
              aria-label="Remove this value"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...condition,
                  values: values.filter((_, at) => at !== index),
                })
              }
            >
              ×
            </button>
          )}
        </span>
      ))}
      {max > values.length && (
        <Button
          size="sm"
          variant="link"
          className="prc-add"
          disabled={disabled}
          onClick={() => onChange({ ...condition, values: [...values, ''] })}
        >
          + value
        </Button>
      )}
      {trait?.type === 'string' && max > 0 && (
        <button
          type="button"
          className={isLower ? 'prc-case prc-case--on' : 'prc-case'}
          aria-pressed={Boolean(isLower)}
          title={isLower ? 'Case-insensitive' : 'Case-sensitive'}
          disabled={disabled}
          onClick={() => onChange({ ...condition, isLower: !isLower })}
        >
          Aa
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          className="prc-x"
          aria-label="Remove this condition"
          disabled={disabled}
          onClick={onRemove}
        >
          ×
        </button>
      )}
    </div>
  );
}
